<template>
  <div class="w-full bg-[#f7f9fb]">
    <Head title="" />
    <!-- Hero Section -->
    <section
      v-if="heroRecipe"
      class="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-16"
    >
      <div class="flex flex-col md:flex-row gap-8 md:gap-12 items-center">
        <!-- Left: Text content -->
        <div class="flex-1 flex flex-col gap-5 md:gap-6 md:pb-10 w-full">
          <!-- Badge -->
          <div class="inline-flex">
            <div class="px-4 py-1.5 rounded-full bg-[#fea619]">
              <span
                class="font-bold text-[14px] text-[#684000] tracking-[0.7px] uppercase"
              >
                UTVALT RECEPT
              </span>
            </div>
          </div>

          <!-- Heading -->
          <h1
            class="font-[Poppins] font-extrabold text-[#191c1e] tracking-[-2px] leading-[1.2] m-0"
            style="font-size: clamp(36px, 6vw, 72px)"
          >
            {{ heroRecipe.title }}
          </h1>

          <!-- Description -->
          <p
            class="text-[18px] text-[#3c4947] leading-[30px] max-w-[512px] m-0"
          >
            {{ heroRecipe.ingress }}
          </p>

          <!-- CTA row -->
          <div class="flex flex-wrap items-center gap-5 pt-2">
            <Link
              :href="heroRecipe.path"
              class="flex items-center gap-2 px-8 py-4 rounded-xl bg-[#14b8a6] no-underline hover:opacity-90 transition-opacity"
            >
              <span class="font-bold text-[16px] text-white"
                >Se hela receptet</span
              >
              <ArrowRight :size="16" color="white" />
            </Link>

            <div
              v-if="heroRecipe.prep_time || heroRecipe.cook_time"
              class="flex gap-6 px-4"
            >
              <div>
                <div
                  class="font-bold text-[10px] text-[#6c7a77] tracking-[1px] uppercase"
                >
                  TID
                </div>
                <div class="font-bold text-[16px] text-[#191c1e]">
                  {{
                    (heroRecipe.prep_time || 0) + (heroRecipe.cook_time || 0)
                  }}
                  min
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Image -->
        <div class="flex-1 relative min-w-0 w-full">
          <div
            class="overflow-hidden rounded-[24px] md:rounded-[32px]"
            style="box-shadow: 0px 25px 50px -12px rgba(0, 0, 0, 0.25)"
          >
            <img
              :src="heroRecipe.cover_image?.xlarge"
              :alt="heroRecipe.title"
              class="w-full object-cover"
              style="height: clamp(260px, 45vw, 580px)"
            />
          </div>

          <!-- Emma tipsar card -->
          <div
            v-if="heroTip || heroRecipe.ingress"
            class="hidden md:flex absolute bottom-[-24px] left-[-24px] flex-col gap-3 p-6 rounded-3xl max-w-[300px] bg-white"
            style="
              box-shadow:
                0px 20px 25px -5px rgba(0, 0, 0, 0.1),
                0px 8px 10px -6px rgba(0, 0, 0, 0.1);
            "
          >
            <div class="flex items-center gap-3">
              <div
                class="rounded-full flex items-center justify-center shrink-0 w-10 h-10 bg-teal-500/10"
              >
                <PhLightbulb class="text-teal-500 w-4 h-4" />
              </div>
              <p class="text-[16px] text-[#3c4947] italic leading-[18px] m-0">
                "{{
                  heroTip ||
                  heroRecipe.ingress.slice(0, 90) +
                    (heroRecipe.ingress.length > 90 ? "..." : "")
                }}"
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Search Section -->
    <section class="px-4 md:px-6 pb-12 md:pb-16">
      <div
        class="max-w-[1232px] mx-auto flex flex-col items-center p-8 md:p-12 rounded-[32px] md:rounded-[48px] bg-[#f2f4f6]"
      >
        <h2
          class="font-[Poppins] font-bold text-[#191c1e] text-center mb-6 md:mb-8 m-0"
          style="font-size: clamp(20px, 4vw, 30px)"
        >
          Vad är du sugen på idag?
        </h2>
        <form
          :action="recipesPath"
          method="get"
          class="relative w-full max-w-[672px]"
        >
          <input
            type="text"
            name="search"
            placeholder="Sök bland hundratals recept..."
            class="w-full py-4 md:py-[22px] pl-14 pr-6 rounded-2xl outline-none bg-white shadow-sm text-[16px] text-gray-500"
          />
          <PhMagnifyingGlass
            class="absolute left-5 top-1/2 -translate-y-1/2 text-[#6C7A77] w-[18px] h-[18px]"
          />
        </form>
      </div>
    </section>

    <!-- Categories Section -->
    <section
      v-if="categories.length > 0"
      class="max-w-[1280px] mx-auto px-4 md:px-6 pb-14 md:pb-20"
    >
      <div class="flex items-end justify-between mb-6 md:mb-8">
        <div>
          <h2
            class="font-[Poppins] font-extrabold text-[#191c1e] m-0"
            style="font-size: clamp(24px, 4vw, 36px)"
          >
            Utforska Kategorier
          </h2>
          <p class="text-[16px] text-[#3c4947] mt-2 m-0">
            Hitta inspiration för varje tillfälle
          </p>
        </div>
        <Link
          :href="categoriesPath"
          class="flex items-center gap-2 no-underline hover:opacity-80 transition-opacity shrink-0 ml-4"
        >
          <span class="hidden sm:inline font-bold text-[16px] text-[#14b8a6]"
            >Visa alla kategorier</span
          >
          <ArrowRight :size="16" color="#14b8a6" />
        </Link>
      </div>

      <div
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-8"
      >
        <Link
          v-for="category in categories"
          :key="category.id"
          :href="category.path"
          class="relative overflow-hidden rounded-[24px] md:rounded-[32px] block no-underline hover:scale-[1.02] transition-transform duration-300 cursor-pointer"
          style="height: clamp(220px, 40vw, 520px)"
        >
          <img
            v-if="category.cover_image"
            :src="category.cover_image"
            :alt="category.name"
            class="w-full h-full object-cover"
            loading="lazy"
          />
          <div v-else class="w-full h-full bg-teal-100" />
          <div
            class="absolute inset-0 flex flex-col justify-end p-6 md:p-8"
            style="
              background: linear-gradient(
                to top,
                rgba(0, 0, 0, 0.8) 0%,
                rgba(0, 0, 0, 0.2) 50%,
                rgba(0, 0, 0, 0) 100%
              );
            "
          >
            <h3
              class="font-[Poppins] font-bold text-white m-0"
              style="font-size: clamp(20px, 3vw, 30px); margin-bottom: 6px"
            >
              {{ category.name }}
            </h3>
            <p v-if="category.subtitle" class="text-[14px] text-white m-0">
              {{ category.subtitle }}
            </p>
          </div>
        </Link>
      </div>
    </section>

    <!-- Featured Recipes -->
    <section class="max-w-[1280px] mx-auto px-4 md:px-6 pb-14 md:pb-20">
      <div class="flex items-center justify-between mb-6 md:mb-8">
        <h2
          class="font-[Poppins] font-bold text-[#191c1e] m-0"
          style="font-size: clamp(20px, 3vw, 30px)"
        >
          Senaste recepten
        </h2>
      </div>

      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
        <template v-if="recipes.length > 0">
          <RecipeCard
            v-for="recipe in recipes"
            :key="recipe.id"
            :recipe="recipe"
            :current-user="currentUser"
            :csrf-token="csrfToken"
          />
        </template>
        <div v-else class="text-xl col-span-full text-[#3c4947]">
          Inga utvalda recept ännu
        </div>

        <div v-if="currentUser?.admin" class="rounded-2xl bg-[#f2f4f6]">
          <a
            :href="editLandingPath"
            class="h-full w-full content-center flex hover:bg-slate-200 min-h-[200px] rounded-2xl"
          >
            <PhPencilSimple class="w-1/6 h-1/6 m-auto" />
          </a>
        </div>
      </div>

      <div class="flex justify-start">
        <Link
          :href="recipesPath"
          class="flex items-center gap-2 px-6 py-3 rounded-xl border border-[#e6e8ea] no-underline hover:bg-[#f2f4f6] transition-colors"
        >
          <span class="font-bold text-[14px] text-[#191c1e]"
            >Visa alla recept</span
          >
          <ArrowRight :size="14" color="#191c1e" />
        </Link>
      </div>
    </section>

    <!-- About Section -->
    <section
      v-if="about?.text || about?.image_1 || about?.image_2"
      class="max-w-[1280px] mx-auto px-4 md:px-6 pb-16 md:pb-24"
    >
      <div class="flex flex-col md:flex-row gap-8 items-stretch">
        <!-- Images -->
        <div class="flex gap-4 md:w-1/2 md:shrink-0 items-start">
          <div
            v-if="about.image_1"
            class="flex-1 overflow-hidden rounded-3xl"
            style="box-shadow: 0px 25px 50px -12px rgba(0, 0, 0, 0.25)"
          >
            <img
              :src="about.image_1"
              alt="Om Emmalagat bild 1"
              class="w-full object-cover"
              style="height: 500px"
              loading="lazy"
            />
          </div>
          <div
            v-if="about.image_2"
            class="flex-1 overflow-hidden rounded-3xl mt-12"
            style="box-shadow: 0px 25px 50px -12px rgba(0, 0, 0, 0.25)"
          >
            <img
              :src="about.image_2"
              alt="Om Emmalagat bild 2"
              class="w-full object-cover"
              style="height: 500px"
              loading="lazy"
            />
          </div>
        </div>

        <!-- Text -->
        <div class="flex flex-col justify-center gap-4 md:pl-4">
          <h2
            class="font-[Poppins] font-extrabold text-[#191c1e] m-0"
            style="font-size: clamp(24px, 4vw, 36px)"
          >
            Om Emmalagat
          </h2>
          <p
            v-if="about.text"
            class="text-[16px] text-[#3c4947] leading-[28px] m-0"
            style="max-width: 480px"
          >
            {{ about.text }}
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { Head, Link } from "@inertiajs/vue3";
import {
  PhPencilSimple,
  PhMagnifyingGlass,
  PhLightbulb,
} from "@phosphor-icons/vue";
import { ArrowRight } from "lucide-vue-next";
import AppLayout from "@/layouts/AppLayout.vue";
import RecipeCard from "@/components/recipe/RecipeCard.vue";

defineProps({
  heroRecipe: { type: Object, default: null },
  heroTip: { type: String, default: null },
  recipes: { type: Array, default: () => [] },
  categories: { type: Array, default: () => [] },
  about: { type: Object, default: () => ({}) },
  currentUser: { type: Object, default: null },
  csrfToken: { type: String, default: "" },
  editLandingPath: { type: String, default: "" },
  recipesPath: { type: String, default: "" },
  categoriesPath: { type: String, default: "" },
});

defineOptions({
  layout: AppLayout,
});
</script>
